import { CircleButton } from "@components/circle-button";
import { LorasType } from "@features/loras/type";
import { getLoraPreview } from "@features/loras/use-loras";
import { usePopularLoras } from "@features/popular/use-popular";
import { useNavigateAlt } from "@features/utils";
import { Button, ScrollArea, Text, TextField } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

interface LoraSearchStyleContentProps {
  onSelect: () => void;
  onNavigate?: (id: string, model: LorasType) => void;
}

export const LoraSearchStyleContent = ({
  onSelect,
  onNavigate,
}: LoraSearchStyleContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateAlt();
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { site } = useParams();

  const lorasQuery = usePopularLoras("lora_style", search);
  const hasNextPage = !!lorasQuery.hasNextPage;
  const isFetchingNextPage = lorasQuery.isFetchingNextPage;

  const loras = lorasQuery.data?.pages.flatMap((page) => page) || [];

  // Auto focus the search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current?.focus?.();
        }
      }, 100);
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      <TextField.Root
        className="mb-4"
        ref={searchInputRef}
        placeholder={t("search.styles.placeholder", "Search public styles...")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <ScrollArea className="flex-1" scrollbars="vertical">
        {lorasQuery.isLoading ? (
          <div className="py-8 text-center">
            <Text>{t("general.loading")}</Text>
          </div>
        ) : loras.length === 0 ? (
          <div className="py-8 text-center">
            <Text>{t("search.styles.no_results", "No styles found")}</Text>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
            {loras.map((lora) => (
              <CircleButton
                key={lora.id}
                onClick={(e) => {
                  if (onNavigate) {
                    onNavigate(lora.id, lora);
                  } else {
                    navigate(
                      `/${site}/camera/new?mode=classic&style=${lora.id}`,
                      e
                    );
                  }
                  onSelect();
                }}
                size="4"
                src={getLoraPreview(lora.id, lora.preview_status)}
                title={lora.name}
                loading={
                  lora.status === "pending" ||
                  lora.preview_status !== "completed"
                }
              />
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="mt-4 text-center">
            <Button
              disabled={isFetchingNextPage}
              onClick={() => lorasQuery.fetchNextPage()}
            >
              {isFetchingNextPage
                ? t("general.loading")
                : t("general.load_more")}
            </Button>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
